import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import PaymentViewController from 'app/src/controllers/payments/PaymentViewController';
import PaymentTotalViewController from 'app/src/controllers/payments/PaymentTotalViewController';
import GlobalEvents from 'app/GlobalEvents';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import SaleCalculationHelper from 'app/src/payments/SaleCalculationHelper';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import PaymentOperations from 'app/src/payments/PaymentOperations';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import PaymentType from 'app/src/data/models/PaymentType';
import CreditAccount from 'app/src/data/models/CreditAccount';
import Localize from 'app/src/localize/Localize';
import Format from 'app/src/data/Format';
import $ from 'jquery';

import AddDepositModal from 'payments/components/AddDepositModal';
import { createReactRenderer } from 'helpers/react-renderer';

// These must match the values indicated by the webPOS legacy UI.  If we subsume responsibility
// for the deposits UI in the future then this is no longer necessary.
const _DepositPaymentTypes = {
  CASH: 'cash_payment',
  CREDIT_CARD: 'credit_card',
  GIFT_CARD: 'gift_card',
  USER_DEFINED: 'payment_type_',
};

// Here are listed the unsupported bank card types
// for the refund deposit. Other unsupported card type
// should be listed here. This piece of logic should be
// replaced in the future by a multi-dimensional state machine
// based on values such as the card type, the payment type, the processor, etc. to avoid
// having to keep this array up to date manually.
// The same list is present in withdraw.php
const NOT_SUPPORTED_DEPOSIT_REFUND_CARD_TYPE = [PaymentTransaction.SupportedCardType.INTERAC];

export default class PaymentController extends BasePaymentController {
  constructor(saleManager, paymentProvider) {
    super(saleManager);

    this._paymentProvider = paymentProvider;

    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.ADD_DEPOSIT,
      this.onAddDeposit
    );
    this.listenTo(
      this._globalChannel.vent,
      GlobalEvents.Events.register.ADD_DEPOSIT_REFUND,
      this.onDepositRefund
    );
  }

  /**
   * Custom cleanup code when the PaymentController is dismissed.
   */
  destroy() {
    // need to cleanup the payment view controllers when closing
    this.closePayments();

    super.destroy();
  }

  /**
   *
   * @param {string} amount   The amount to refund for a deposit
   * @param {object} options  An options hash to provide details about the deposit
   * @param {number} options.paymentTypeID    The payment type ID of the deposit refund
   * @param {number} options.originalPaymentID   The sale payment ID of an original payment; used for
   *                                                  credit card refunds
   */
  onDepositRefund(
    amount,
    options = {
      cardNumber: null,
      cardType: null,
      paymentTypeID: 0,
      originalPaymentID: 0,
      paymentID: null,
    }
  ) {
    // This flag checks that the specified card type requires
    // cardholder interaction in order to properly
    // refund the deposit. In the case of the deposit
    // refund any card type in NOT_SUPPORTED_DEPOSIT_REFUND_CARD_TYPE
    // will likely throw an error message to the user that the card type
    // is not supported.
    const isCardTypeNotSupportedForDepositRefund =
      options && NOT_SUPPORTED_DEPOSIT_REFUND_CARD_TYPE.includes(options.cardType);
    const paymentID = options && options.paymentID;
    // deposit refunds are expressed as negative deposits
    let paymentAmount = Format.parseDecimal(amount, 0)
      .abs()
      .negated();
    // This is a deposit refund for credit card payment so obtain the credit card payment type ID
    let paymentTypeID = options.paymentTypeID
      ? options.paymentTypeID
      : PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.TERMINAL);

    if (!paymentAmount.isZero() && paymentTypeID) {
      // the data coming from the legacy code is different to identify the type of payment
      // when doing deposits vs refund deposits.  Normalize to a payment type.
      let paymentType = PaymentTypeManager.getPaymentType(paymentTypeID, true);
      let deposit = this._createDeposit(paymentAmount, paymentType, true);

      if (deposit) {
        if (
          (paymentType && paymentType.get('type') === PaymentType.Types.CREDIT_CARD) ||
          (paymentType && paymentType.get('code') === PaymentType.Codes.CREDIT_CARD)
        ) {
          deposit.depositPayment.model.set({
            refPaymentID: options.originalPaymentID,
            saleID: this._saleManager.sale.id,
          });

          if (paymentID) {
            deposit.depositPayment.model.set({
              paymentID,
            });
          }

          if (options.cardNumber) {
            deposit.depositPayment.model.cardNumber = options.cardNumber;
          }
        }

        if (
          deposit.depositPayment.isServicePayment() ||
          (!this._paymentProvider.activeGatewaySupportsRefundByReference &&
            (paymentType.get('type') === PaymentType.Types.CREDIT_CARD ||
              paymentType.get('code') === PaymentType.Codes.CREDIT_CARD) &&
            this._paymentProvider.activeGateway)
        ) {
          // This code should not be reachable since we disabled the selection
          // of the unsupported card types in the backend directly, but it does add
          // a second level of protection if someone manages to bypass
          // the disabled select option.
          if (isCardTypeNotSupportedForDepositRefund) {
            alertUser(
              Localize.translate(
                'Refunding a deposit on this type of card is not supported. Please refund the customer by cash.'
              )
            );
          } else {
            PaymentOperations.doRefundDepositOperation(
              deposit,
              this._saleManager,
              this._paymentProvider
            );
          }
        } else {
          if (this._saleManager.deposits.addDepositRefund(deposit)) {
            flashNotifier(
              'success',
              Localize.translate(
                `Deposit Refund Of <var>${Format.formatMoney(amount)}</var> Added.`
              ),
              4
            );
          } else {
            alertUser(Localize.translate('Could not add the refund.'));
          }
        }
      }
    }
  }

  /**
   * Handler when the add deposit event is triggered.
   */
  onAddDeposit(isAccountPayment) {
    // create a credit account for the customer if necessary
    let deferred = $.Deferred();
    if (!this._saleManager.creditAccountID) {
      let creditAccount = new CreditAccount({
        name: this._saleManager.customer.fullName,
        customerID: this._saleManager.customer.id,
      });

      // we have to create a customer credit account through the customer
      // endpoint using a realtion which restricts what can be set when doing
      // so so we need to clean out those restricted parameters.
      creditAccount.unset('archived');
      creditAccount.unset('description');
      creditAccount.unset('giftCard');
      creditAccount.unset('balance');

      this._saleManager.customer.setRelation('CreditAccount', creditAccount);

      this._saleManager.customer
        .save()
        .done(() => {
          deferred.resolve();
        })
        .fail(response => {
          deferred.reject(response);
        });
    } else {
      deferred.resolve();
    }

    deferred
      .done(() => {
        // Show the deposit modal to retrieve the amount
        if (!this.addDepositModal) {
          this._saleManager.customer
            .getOpenOrders()
            .done(openOrders => {
              let totalOpenOrders = Format.parseDecimal(0);
              if (openOrders && openOrders.find) {
                const allOpenOrderRecord = openOrders.find(
                  openOrderTotal => openOrderTotal.type === 'all'
                );
                if (allOpenOrderRecord) {
                  totalOpenOrders = Format.parseDecimal(allOpenOrderRecord.total, 0);
                }
              }

              const RenderedComponent = createReactRenderer({
                component: AddDepositModal,
                selector: '[data-react-root="ReactModal"]',
                props: {
                  onAddDeposit: amount => {
                    this._doAddDeposit(amount, isAccountPayment);
                  },
                  onHide: () => {
                    delete this.addDepositModal;
                    RenderedComponent.unmount();
                  },
                  isAccountPayment,
                  currency: Localize.getCurrencySymbol(),
                  customerName: this._saleManager.customer.fullName,
                  creditBalance: this._saleManager.customer.creditAccountBalance,
                  openOrders: totalOpenOrders,
                },
              });
              this.addDepositModal = RenderedComponent.mount();
            })
            .fail(() => {
              PaymentServiceAlert.alert('Could not retrive the open orders');
            });
        }
      })
      .fail(response => {
        PaymentServiceAlert.alert(response);
      });
  }

  /**
   * Adds a deposit of the amount specified to the sale.
   *
   * @param {Decimal} amount      The amount of the deposit.
   * @private
   */
  _doAddDeposit(amount, isAccountPayment) {
    let paymentAmount = Format.parseDecimal(amount, 0);

    if (!paymentAmount.isZero()) {
      let deposit = this._createDeposit(paymentAmount);

      if (deposit) {
        this._saleManager
          .addDeposit(deposit, isAccountPayment)
          .done(() => {
            window.flashNotifier(
              'success',
              Localize.translate(
                `Credit Account Deposit Of <var>${Format.formatMoney(amount)}</var> Added.`
              ),
              4
            );
          })
          .fail(() => {
            alertUser(Localize.translate('Could not add the deposit.'));
          });
      }
    }
  }

  /**
   * Creates a deposit.  For types (such as cash) where no additional interaction is required, the payment
   * is simply created and returned.  When additional interaction is required (e.g. credit cards) the
   * appropriate UI will be presented in order to obtain the deposit payment.
   *
   *  @param {Decimal} amount -  to be deposited.
   *  @param {PaymentType} paymentType - They type of deposit payment (used for refunds)
   */
  _createDeposit(amount, paymentType = null, isRefund = false) {
    let paymentAmount = Format.parseDecimal(amount, 0);
    let deposit = null;

    if (paymentType) {
      switch (paymentType.get('type')) {
        case PaymentType.Types.CASH:
          deposit = PaymentFactory.createPayment(
            PaymentTypeManager.Types.DEPOSIT,
            PaymentFactory.createPayment(PaymentTypeManager.Types.CASH, paymentAmount),
            this._saleManager.creditAccountID
          );
          break;

        case PaymentType.Types.CREDIT_CARD:
          if (isRefund) {
            deposit = PaymentFactory.createPayment(
              PaymentTypeManager.Types.DEPOSIT,
              PaymentFactory.createPayment(PaymentTypeManager.Types.TERMINAL, paymentAmount),
              this._saleManager.creditAccountID
            );
          } else {
            this._paymentViewController.startCreditCardDeposit(paymentAmount);
          }
          break;

        case PaymentType.Types.GIFT_CARD:
          if (!isRefund) {
            this._paymentViewController.startGiftCardDeposit(paymentAmount);
          } else {
            console.error('Gift card deposit refunds are not supported');
          }
          break;

        case PaymentType.Types.USER_DEFINED:
          deposit = PaymentFactory.createPayment(
            PaymentTypeManager.Types.DEPOSIT,
            PaymentFactory.createPayment(
              PaymentTypeManager.Types.USER_DEFINED,
              paymentAmount,
              paymentType.id
            ),
            this._saleManager.creditAccountID
          );
          break;

        default:
          break;
      }
    } else {
      // just create a straight-up deposit (credit).  The user will need to balance
      // that out with an actual payment at some point.  We use a pending payment as a
      // placeholder but it's essentially meaningless since that would never be sent
      // to the server.  It's used only to determine the amount of the deposit.
      deposit = PaymentFactory.createPayment(
        PaymentTypeManager.Types.DEPOSIT,
        PaymentFactory.createPayment(PaymentTypeManager.Types.PENDING, paymentAmount),
        this._saleManager.creditAccountID
      );
    }

    return deposit;
  }

  /**
   * Enables any controls that are rendered by the Legacy UI that trigger actions in the new code.
   * This is done to prevent undesirable affects while the application is initializing.
   */
  enableLegacyControls() {
    $('#makePaymentButton').prop('disabled', false);
    $('#addDepositButton').prop('disabled', false);
    $('#refundDepositButton').prop('disabled', false);
  }

  /**
   *  Create and show the individual payment views this controller manages.
   */
  showPayments() {
    let creditAccountOperation = $.Deferred().resolve();
    // If this is a refund operation, and the customer is attached but does not have a creditAccountID
    // attach a creditAccountID for this customer.
    if (
      this._saleManager.isRefundSale() &&
      this._saleManager.customer &&
      !this._saleManager.creditAccountID
    ) {
      let creditAccount = new CreditAccount({
        name: this._saleManager.customer.fullName,
        customerID: this._saleManager.customer.id,
      });

      // we have to create a customer credit account through the customer
      // endpoint using a relation which restricts what can be set when doing
      // so so we need to clean out those restricted parameters.
      creditAccount.unset('archived');
      creditAccount.unset('description');
      creditAccount.unset('giftCard');
      creditAccount.unset('balance');

      this._saleManager.customer.setRelation('CreditAccount', creditAccount);
      creditAccountOperation = $.Deferred();
      this._saleManager.customer
        .save()
        .fail(jqXHR => {
          console.log(
            'Unable to create a credit account for the customer.  Ensure that the customer has a name or address assigned.'
          );
        })
        .always(() => {
          // resolve even if it fails but we do show an error
          creditAccountOperation.resolve();
        });
    }

    creditAccountOperation
      .done(() => {
        const availableCreditForLayawayOrSO = SaleCalculationHelper.getAvailableCreditForLayawayOrSO(
          this._saleManager
        );

        if (
          this._saleManager.creditAccountID &&
          availableCreditForLayawayOrSO.isGreaterThan(0) &&
          !this._saleManager.deposits.hasDeposits
        ) {
          // Adds a credit account payment to cover layaways and deposits in the sale.  This has the affect
          // of auto-filling the credit account field in the default view.  To be consistent with the old-UI,
          // the payment will always auto-fill whenever the payment block is shown.
          let payment = PaymentFactory.createPayment(
            PaymentTypeManager.Types.CREDIT_ACCOUNT,
            availableCreditForLayawayOrSO
          );
          this._saleManager.addPayment(payment);
        }

        this._paymentViewController = new PaymentViewController(
          this._saleManager,
          this._paymentProvider
        );
        this._paymentViewController.show();

        this._paymentTotalViewController = new PaymentTotalViewController(this._saleManager);
        this._paymentTotalViewController.show();
      })
      .fail(jqXHR => {
        // the legacy server UI holds onto the payments state so we need to go through
        // legacy functions to 'cancel' payments
        window.merchantos.register.continueSale().always(() => {
          PaymentServiceAlert.alert(jqXHR);
        });
      });
  }

  /**
   *  Destroy the individual payment views this controller manages if they exist.
   */
  closePayments() {
    if (this._paymentViewController) {
      this._paymentViewController.destroy();
      this._paymentViewController = null;
    }

    if (this._paymentTotalViewController) {
      this._paymentTotalViewController.destroy();
      this._paymentTotalViewController = null;
    }
  }
}
